import type { ActiveTemplateField } from '../../api/types';

import type { ConfidenceLevel } from './tokens';

/**
 * Helpers for coercing, formatting, and inspecting per-field values. Field
 * values are stored untyped (`unknown`) on the active template; these keep the
 * type-specific logic in one place so renderers and the edit form agree.
 */

/** A value counts as "filled" when it is non-null and not an empty string/array. */
export function isFilled(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/** Number of fields with a non-empty current value. */
export function filledFieldCount(fields: ActiveTemplateField[]): number {
  return fields.filter((f) => isFilled(f.currentValue)).length;
}

/**
 * Confidence level to display for a field, in precedence order:
 *  - empty value → `empty` ("Not found · add manually")
 *  - human-edited (lastEditedByAi === false) → `edited` ("Edited by you")
 *  - otherwise the AI confidence (`high` | `medium` | `low`), or null.
 */
export function deriveFieldConfidence(
  field: Pick<
    ActiveTemplateField,
    'currentValue' | 'lastEditedByAi' | 'confidence'
  >,
): ConfidenceLevel | null {
  if (!isFilled(field.currentValue)) return 'empty';
  if (field.lastEditedByAi === false) return 'edited';
  return field.confidence;
}


/** A table value is an array of row objects; rows default to empty. */
export function asTableRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (r): r is Record<string, unknown> =>
      typeof r === 'object' && r !== null && !Array.isArray(r),
  );
}

/** A row counts as empty when every cell is null, '', or an empty array. */
function isEmptyRow(row: Record<string, unknown>): boolean {
  return Object.values(row).every(
    (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0),
  );
}

/**
 * Drop fully-empty rows from a table value before saving so an added-but-never-
 * filled row acts as if it never existed (the backend otherwise rejects it when
 * a column is required).
 */
export function stripEmptyTableRows(value: unknown): Record<string, unknown>[] {
  return asTableRows(value).filter((row) => !isEmptyRow(row));
}

/** A list value (string_list / number_list / multi_select) as an array. */
export function asList(value: unknown): (string | number)[] {
  if (!Array.isArray(value)) return [];
  return value as (string | number)[];
}

/** Column/field types whose value must be an array, not a scalar string. */
const LIST_COLUMN_TYPES = new Set([
  'multi_select',
  'string_list',
  'number_list',
  'list',
]);

/** Strings the AI may emit for a boolean value, normalized to `true`/`false`. */
const BOOL_TRUE = new Set(['true', 'yes', 'y', '1', 'on']);
const BOOL_FALSE = new Set(['false', 'no', 'n', '0', 'off']);

/** Coerce a value into a typed list (splitting a comma-joined string). */
function toList(type: string, value: unknown): (string | number)[] {
  const parts: unknown[] = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
  if (type === 'number_list') {
    return parts
      .map((p) => (typeof p === 'number' ? p : Number(String(p).trim())))
      .filter((n): n is number => Number.isFinite(n));
  }
  return parts.map((p) => String(p).trim()).filter(Boolean);
}

/**
 * Coerce a scalar value to the shape its declared `type` requires before saving,
 * so an AI-extracted (or legacy) value in the wrong shape doesn't trip the
 * backend's strict validator. Used for both table cells and whole scalar fields.
 *
 *  - list types (`string_list` etc.) → array (a comma string is split;
 *    `multi_select` is additionally filtered to `options`);
 *  - `boolean` → a recognized truthy/falsy string becomes a real boolean;
 *    anything unrecognizable is dropped to `null` (it can't be persisted);
 *  - `number` → a numeric string becomes a number; non-numeric → `null`;
 *  - `select` → a value outside `options` is dropped to `null`;
 *  - everything else (`string`, `date`, `email`, `phone`, …) is returned as-is
 *    — a malformed date/email is surfaced as a visible schema error instead.
 *
 * A null/empty value always passes through so the backend still reads it as
 * "absent" (partial saves never require a value).
 */
export function coerceScalarForSave(
  type: string,
  value: unknown,
  options: string[] | null = null,
): unknown {
  if (value == null || value === '') return value;

  if (LIST_COLUMN_TYPES.has(type)) {
    let list = toList(type, value);
    if (type === 'multi_select' && options && options.length > 0) {
      const allowed = new Set(options);
      list = list.filter((v) => typeof v === 'string' && allowed.has(v));
    }
    return list;
  }

  if (type === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const s = value.trim().toLowerCase();
      if (BOOL_TRUE.has(s)) return true;
      if (BOOL_FALSE.has(s)) return false;
    }
    return null;
  }

  if (type === 'number') {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
      const n = Number(value.trim());
      return value.trim() !== '' && Number.isFinite(n) ? n : null;
    }
    return null;
  }

  if (type === 'select') {
    if (
      options &&
      options.length > 0 &&
      typeof value === 'string' &&
      !options.includes(value)
    )
      return null;
    return value;
  }

  return value;
}

/**
 * Normalize every cell of a table value to its column's declared type before
 * saving, so a column that arrived in the wrong shape (a list column as a
 * comma-joined string, a boolean column as `"true"`, …) is stored as the type
 * the backend's strict validator requires. Only keys present on a row are
 * touched; the row set itself is unchanged.
 */
export function normalizeTableCells(
  rows: Record<string, unknown>[],
  columns: { key: string; type: string; options?: string[] | null }[],
): Record<string, unknown>[] {
  if (columns.length === 0) return rows;
  return rows.map((row) => {
    const next: Record<string, unknown> = { ...row };
    for (const col of columns) {
      if (col.key in next)
        next[col.key] = coerceScalarForSave(col.type, next[col.key], col.options ?? null);
    }
    return next;
  });
}

/**
 * A field's authoring `example` rendered as placeholder text, or null when the
 * example isn't a simple scalar (e.g. table row objects). Callers fall back to
 * the generic "add manually" hint when this returns null. Arrays (list-field
 * examples) collapse to a comma-separated preview.
 */
export function examplePlaceholder(example: unknown): string | null {
  if (typeof example === 'string') {
    const trimmed = example.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof example === 'number' && Number.isFinite(example)) {
    return String(example);
  }
  if (Array.isArray(example)) {
    const parts = example
      .filter((e) => typeof e === 'string' || typeof e === 'number')
      .map(String)
      .filter((s) => s.trim() !== '');
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
}

/** Display string for a scalar cell/value. */
export function formatScalar(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? '✓' : '✕';
  return String(value);
}

/**
 * Coerce a raw text-input string into the typed value for a scalar field.
 * Empty input becomes null so the field reads as "not filled".
 */
export function coerceScalar(type: string, raw: string): unknown {
  const trimmed = raw;
  if (trimmed === '') return null;
  if (type === 'number') {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return trimmed;
}
